
import { Component, Mixins } from "vue-property-decorator";
import NavHeader from "@/components/NavHeader.vue";
import PaginatedSearch, { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import NotificationBrandingTemplate from "@/types/sv-data/notification-templates/NotificationBrandingTemplate";
import NotificationTemplatePagedRequest from "@/types/sv-data/notification-templates/NotificationTemplatePagedRequest";
import NotificationBrandingTemplatePagedRequest from "@/types/sv-data/notification-templates/NotificationBrandingTemplatePagedRequest";
import NotificationTemplateApi from "@/services/api.notificationTemplate.service";
import { NotificationSystemPrefs, PaginatedSearchQueryParams, TenantSystemPrefs, UserPermissions } from "@/store/types";
import { Getter, namespace, Action, State } from "vuex-class";
import GenericTable, { TableHeader } from "@/components/table/generic-table.vue";
import { ModalItem } from "@/components/table/generic-update-modal.vue";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import { AreaNode } from "@/types/sv-data/groups/AreaNode";
import RichTextEditor from "@/components/form/RichTextEditor.vue";
import SubjectLineTokenField from "@/components/notification-templates/SubjectLineTokenField.vue";
import RichTextEditorPlaceholder from "@/types/RichTextEditorPlaceholder";
import NotificationTemplateTypeId from "@/types/sv-data/enums/NotificationTemplateTypeIds";
import NotificationTemplatePreviewRequest from "@/types/sv-data/notification-templates/NotificationTemplatePreviewRequest";
import NotificationDocumentTypeId from "@/types/sv-data/enums/NotificationDocumentTypeIds";
import axios, { CancelTokenSource } from "axios";
import api from "@/services/api.service";
import ContactGroup from "@/types/sv-data/contact-groups/ContactGroup";
import { cloneDeep, debounce } from "lodash";
import { getDebouncePeriod } from "@/utils/debounce-helper";
import { truncateString } from "@/filters";
import CustomFieldTableView from "@/components/custom-fields/CustomFieldModalTableView.vue";
import { CustomFieldDto, CustomFieldTableTypes } from "@/store/custom-fields/types";
import NotificationPlaceholderMixin from "@/mixins/NotificationMixin";

const CustomFields = namespace("customFields");

@Component({
	filters: { truncateString },
	components: {
		"nav-header": NavHeader,
		"generic-table": GenericTable,
		"custom-field-modal-table-view": CustomFieldTableView,
	},
})
export default class NotificationTemplateSetup extends Mixins(PaginatedSearch,  NotificationPlaceholderMixin) {
	$refs!: {
		customFieldTableView: CustomFieldTableView;
	};

	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter("getNotificationSystemPrefs") notificationSystemPrefs: NotificationSystemPrefs;

	@CustomFields.State private notificationTemplateCustomFields: CustomFieldDto[];
	@CustomFields.Action retrieveCustomFields: ({ tableType, live, skipCancel }) => Promise<void>;
		
	@Action getTenantSystemPrefs: (live: boolean) => Promise<void>;
	@Action createOrUpdateTenantSystemPrefs: (updatedPrefs: TenantSystemPrefs) => Promise<void>;

	private notificationTemplates: NotificationTemplate[] = [];
	private notificationTemplatesForTable: NotificationTemplate[] = [];
	private notificationBrandingTemplates: NotificationBrandingTemplate[] = [];

	private manualFields: RichTextEditorPlaceholder[] = [];
	private customFields: RichTextEditorPlaceholder[] = [];
	private selectedNotificationTemplate: NotificationTemplate = null;
	private isLoading: boolean = false;
	private totalRecords: number = 0;
	private previewEventId: number = null;
	private contactGroupSearchToken: CancelTokenSource = null;
	private contactGroups: ContactGroup[] = [];
	private readonly customFieldTableType: number = CustomFieldTableTypes.NotificationTemplate;

	private async mounted(): Promise<void> {

		if (!this.notificationSystemPrefs)
		{
			await this.getTenantSystemPrefs(true);
		}

		await this.getPaginatedNotificationTemplates();

		if (this.isBrandingTemplatesEnabled) {
			await this.getPaginatedNotificationBrandingTemplates();
		}

		if (this.isContactGroupsEnabled) {
			await this.retrieveContactGroups();
		}

		if (this.isCustomFieldsNotificationTemplatesEnabled) {
			await this.retrieveCustomFields({
				tableType: CustomFieldTableTypes.NotificationTemplate,
				live: true,
				skipCancel: false,
			});
		}
	}

	private destroyed(): void {
		this.notificationTemplates = null;
		this.notificationTemplatesForTable = null;
	}

	private get isNotificationTemplatesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates"]);
	}

	private get isContactGroupsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationGroups"]);
	}

	private get isCustomFieldsNotificationTemplatesEnabled(): boolean {
		return this.getFeature(["CustomFields", "NotificationTemplates"]);
	}

	private get isSubjectLineEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "SubjectLine"]);
	}

	private get isBrandingTemplatesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "BrandingTemplates"]);
	}
	
	private get isSendToAllAsSingleEmailEnabled()
	{
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "SendToAllInSingleEmail"]);
	}

	private get hasNotificationTemplatePermissions(): boolean {
		return this.permissions.canViewNotificationTemplates || this.permissions.canEditNotificationTemplates;
	}

	private get hasCanViewCustomFieldsPermission(): boolean {
		return this.permissions.canViewCustomFields;
	}

	private async createNotificationTemplate(notificationTemplate: NotificationTemplate): Promise<void> {
		await NotificationTemplateApi.createNotificationTemplate(notificationTemplate);
		await this.getPaginatedNotificationTemplates(this.mostRecentSearchParams);
	}

	private async getPaginatedNotificationTemplates(
		paginatedSearchQueryParams?: PaginatedSearchQueryParams
	): Promise<void> {
		this.isLoading = true;
		this.notificationTemplates = [];

		if (!paginatedSearchQueryParams) {
			paginatedSearchQueryParams = {
				page: 1,
				pageSize: 25,
				sortDesc: false,
				sortBy: "Title",
				searchTerm: "",
			};
		}

		const searchParams = this.generateNewPaginatedSearchRequest(paginatedSearchQueryParams);

		const request = { ...searchParams, ...paginatedSearchQueryParams } as NotificationTemplatePagedRequest;

		// Hard coded as no other types exist
		request.notificationTemplateTypeId = NotificationTemplateTypeId.Event;

		this.mostRecentSearchParams = request;

		const results = await NotificationTemplateApi.getNotificationTemplates(request, request.groupId);
		this.totalRecords = results.totalRecords;
		this.notificationTemplates = results.data;

		this.notificationTemplatesForTable = cloneDeep(this.notificationTemplates)
			.map(template =>
				{
					if (template.sendToAllInSingleEmail == null)
					{
						template.sendToAllInSingleEmail = this.notificationSystemPrefs.notificationsDefaultToSendInSingleEmail
					}

					return template;
				}
			);

		this.isLoading = false;
	}

	/**
	 * Retrieves paginated notification branding templates.
	 * @param paginatedSearchQueryParams Optional search params for pagination.
	 */
	private async getPaginatedNotificationBrandingTemplates(
		paginatedSearchQueryParams?: PaginatedSearchQueryParams
	): Promise<void> {
		this.isLoading = true;
		this.notificationBrandingTemplates = [];

		// get a large set of templates to allow for searching as there should be a lot of them
		if (!paginatedSearchQueryParams) {
			paginatedSearchQueryParams = {
				page: 1,
				pageSize: 200,
				sortDesc: false,
				sortBy: "Title",
				searchTerm: "",
			};
		}

		const searchParams = this.generateNewPaginatedSearchRequest(paginatedSearchQueryParams);

		const request = { ...searchParams, ...paginatedSearchQueryParams } as NotificationBrandingTemplatePagedRequest;

		// Hard coded as no other types exist
		request.notificationTemplateTypeId = NotificationTemplateTypeId.Event;
		this.mostRecentSearchParams = request;

		const results = await NotificationTemplateApi.getNotificationBrandingTemplates(request, request.groupId);
		this.totalRecords = results.totalRecords;
		this.notificationBrandingTemplates = results.data;
		this.isLoading = false;
	}

	private async updateNotificationTemplate(notificationTemplate: NotificationTemplate) {

		const originalValues = this.notificationTemplates.find(t => t.notificationTemplateId === notificationTemplate.notificationTemplateId);

		if (originalValues)
		{
			if (originalValues.sendToAllInSingleEmail == null 
				&& notificationTemplate.sendToAllInSingleEmail == this.notificationSystemPrefs.notificationsDefaultToSendInSingleEmail)
			{
				notificationTemplate.sendToAllInSingleEmail = null;
			}
		}

		await NotificationTemplateApi.updateNotificationTemplate(notificationTemplate);
		await this.getPaginatedNotificationTemplates(this.mostRecentSearchParams);
	}

	private async deleteNotificationTemplate(notificationTemplate: NotificationTemplate) {
		await NotificationTemplateApi.deleteNotificationTemplate(notificationTemplate.notificationTemplateId);
		await this.getPaginatedNotificationTemplates(this.mostRecentSearchParams);
	}

	private async requestNotificationPreview(): Promise<void> {
		if (this.selectedNotificationTemplate == null || this.previewEventId < 1) {
			return;
		}
		this.previewEventId = Number(this.previewEventId);

		const request: NotificationTemplatePreviewRequest = {
			notificationTemplateId: this.selectedNotificationTemplate.notificationTemplateId,
			notificationDocumentTypeId: NotificationDocumentTypeId.Pdf,
			manualInputFields: {},
			customFieldInputs: {},
		};

		await NotificationTemplateApi.previewNotificationTemplate(this.previewEventId, request);
	}

	private handleSearchInput = debounce(
		async (searchTerm: string): Promise<void> => {
			await this.retrieveContactGroups(searchTerm);
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true }
	);

	private async retrieveContactGroups(searchTerm: string = ""): Promise<void> {
		const params: PaginatedSearchQueryParams = {
			page: 1,
			pageSize: 50,
			sortBy: "title",
			searchTerm: searchTerm,
		};

		this.contactGroupSearchToken = axios.CancelToken.source();

		const request: PaginatedSearchRequest = {
			params: params,
			cancelToken: this.contactGroupSearchToken.token,
		};

		try {
			this.contactGroups = (await api.getContactGroups(request)).data;
		} catch (err) {
			console.error(err);
			this.$notify({
				type: "error",
				title: "Retrieving Contact Groups",
				text: err.message,
			});
			return;
		}
	}

	private async onModalOpen(value: NotificationTemplate): Promise<void> {
		if (value) {
			this.selectedNotificationTemplate = value;
			this.manualFields = this.selectedNotificationTemplate.manualFields;

			if (value.sendToAllInSingleEmail == null)
			{
				value.sendToAllInSingleEmail = this.notificationSystemPrefs.notificationsDefaultToSendInSingleEmail
			}

		} else {
			this.selectedNotificationTemplate = null;
			this.manualFields = [];
		}
	}

	private concatContactGroupTitles(contactGroups: ContactGroup[]): string {
		return contactGroups.map((cg) => cg.title).join(", ");
	}

	private async openCustomFieldModal(): Promise<void> {
		this.$refs.customFieldTableView.showCustomFieldModal();
	}

	private get getCustomFieldPlaceholders(): RichTextEditorPlaceholder[] {
		this.customFields = [];
		if (
			!this.isCustomFieldsNotificationTemplatesEnabled ||
			!this.hasCanViewCustomFieldsPermission ||
			(this.selectedNotificationTemplate && !this.selectedNotificationTemplate.customFields)
		) {
			return this.customFields;
		}
		if (Array.isArray(this.notificationTemplateCustomFields)) {
			this.notificationTemplateCustomFields.forEach((cf) => {
				this.customFields.push({
					id: cf.id,
					label: cf.title,
				});
			});
		}
		return this.customFields;
	}

	// Only NotificationTemplate custom fields individually marked eligible may be used
	// as subject-line placeholders.
	private get getEligibleSubjectCustomFieldPlaceholders(): RichTextEditorPlaceholder[] {
		if (
			!this.isCustomFieldsNotificationTemplatesEnabled ||
			!this.hasCanViewCustomFieldsPermission ||
			!Array.isArray(this.notificationTemplateCustomFields)
		) {
			return [];
		}

		return this.notificationTemplateCustomFields
			.filter((cf) => cf.options?.eligibleForNotificationSubject === true)
			.map((cf) => ({ id: cf.id, label: cf.title }));
	}

	private get columns(): TableHeader[] {
		const _columns: TableHeader[] = [
			{
				title: "Title",
				key: "title",
				order: 1,
				dataType: "input",
				description: "Notification Template Title",
				sortable: true,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				isTermLabel: true,
			},
			{
				title: "Area",
				key: "groupTitle",
				order: 2,
				dataType: "input",
				description: "Area Title",
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
			},
			{
				title: "Inherited",
				key: "inherited",
				order: 3,
				dataType: "checkbox",
				sortable: true,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
			},
		];

		if (this.isContactGroupsEnabled) {
			_columns.push({
				title: "Default Contact Groups",
				key: "contactGroups",
				order: 4,
				dataType: "input",
				description: "Default Contact Groups",
				sortable: true,
				sortOrder: 0,
				sortOrderReversed: false,
				useCustomCell: true,
				visible: this.isContactGroupsEnabled,
			});
		}

		if (this.isSendToAllAsSingleEmailEnabled) {
			_columns.push({
				title: "Send single email",
				key: "sendToAllInSingleEmail",
				order: 5,
				dataType: "checkbox",
				description: "Send notification to all contacts in single email",
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: this.isSendToAllAsSingleEmailEnabled,
			});
		}

		return _columns;
	}

	private get placeholders(): RichTextEditorPlaceholder[] {
		return [... this.getNotificationPlaceholders].sort((a, b) => a.label.localeCompare(b.label));
	}

	private get modalItems(): ModalItem[] {
		return [
			{
				title: "Title",
				key: "title",
				dataType: "text",
				required: true,
				maxLength: 50,
				description: "Enter a notification template title",
				placeholder: "Notification template title",
			},
			{
				title: "Inherited",
				key: "inherited",
				required: false,
				dataType: "checkbox",
			},
			{
				title: "Area",
				key: "groupId",
				dataType: "component",
				data: AreaTreeSelect,
				required: true,
				props: {
					reduce: (area: AreaNode) => area.id,
					clearable: false,
				},
			},
			{
				title: "Default Contact Groups",
				key: "contactGroups",
				dataType: "vselect3",
				data: this.contactGroups,
				visible: this.isContactGroupsEnabled,
				props: {
					onSearch: (searchTerm: string) => {
						this.handleSearchInput(searchTerm);
					},
					multiple: true,
				},
				placeholder: "Select default contact groups for this notification template",
				description: "Select default contact groups for this notification template",
			},
			{
				title: "Send to all recipients in single email",
				key: "sendToAllInSingleEmail",
				visible: this.isSendToAllAsSingleEmailEnabled,
				required: false,
				dataType: "checkbox",
				defaultValue: this.notificationSystemPrefs.notificationsDefaultToSendInSingleEmail,
			},
			{
				title: "Branding Template",
				key: "brandingTemplateId",
				dataType: "vselect3",
				data: this.notificationBrandingTemplates,
				visible: this.isBrandingTemplatesEnabled,
				props: {
					reduce: (template: NotificationTemplate) => template.notificationTemplateId,
				},
				description: "Select a branding template for this notification template",
				placeholder: "Select a branding template for this notification template",
			},
			{
				title: "Subject Line",
				key: "subjectLine",
				dataType: "component",
				data: SubjectLineTokenField,
				required: false,
				visible: this.isSubjectLineEnabled,
				props: {
					placeholders: this.getSubjectPlaceholders,
					manualFields: this.manualFields,
					customFields: this.getEligibleSubjectCustomFieldPlaceholders,
					showCustomFields: this.isCustomFieldsNotificationTemplatesEnabled && this.hasCanViewCustomFieldsPermission,
					customFieldTokenKind: "CustomField",
				},
			},
			{
				title: "Body",
				key: "body",
				dataType: "component",
				data: RichTextEditor,
				props: {
					placeholders: this.placeholders,
					manualFields: this.manualFields,
					minHeight: "350px",
					customFields: this.getCustomFieldPlaceholders,
				},
			},
		];
	}

	private getNotificationBrandingTemplateTitle(notificationTemplateId: number): string {
		const layout =  this.notificationBrandingTemplates.find((template) => template.notificationTemplateId === notificationTemplateId);

		return layout ? layout.title : "";
	}
}
